import { Autocomplete, Paper, Popper, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { IFilesApiObject } from '../../@types/FilesAPIModels';
import { createLocalizedLabelComparator } from '../../utils/localizedSortUtils';

const useStyles = makeStyles((theme) => ({
  autocomplete: {
    width: '100% !important',
    marginBottom: theme.spacing(3),
    '& .MuiInputBase-root': {
      borderRadius: '16px',
      fontSize: '1rem',
      padding: theme.spacing(1, 2),
      border: '1.5px solid #d3d3d3',
      backgroundColor: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&:hover .MuiInputBase-root': { borderColor: '#b3b3b3' },
    '& .Mui-focused .MuiInputBase-root': { borderColor: '#b3b3b3' },
    '& .MuiAutocomplete-endAdornment': { display: 'none' },
    '& .MuiInputLabel-root': {
      fontSize: '1rem',
      color: '#464646',
      top: '50%',
      transform: 'translate(14px, -50%) scale(1)',
      '&.Mui-focused': {
        color: '#464646',
      },
    },
    '& .MuiInputLabel-shrink': {
      top: 0,
      transform: 'translate(14px, -9px) scale(0.75)',
      backgroundColor: '#fff',
      padding: '0 4px',
      color: '#464646',
    },
    '& .MuiInputBase-input': {
      padding: theme.spacing(1.5, 2),
    },
  },
  popper: {
    marginTop: '0.1rem !important',
    zIndex: 1500,
    overflow: 'visible',
    '& .MuiAutocomplete-listbox': {
      borderRadius: '16px !important',
      overflow: 'auto',
      paddingRight: '3px',
      backgroundColor: '#fff',
    },
    borderRadius: '16px',
  },
  paper: {
    borderRadius: '16px',
    boxShadow: '0px 8px 16px rgba(0,0,0,0.15)',
    border: '1px solid #d3d3d3',
    borderTop: 'none',
    marginTop: '-1px',
    overflow: 'visible',
    backgroundColor: '#fff',
  },
  listbox: {
    maxHeight: '260px',
    padding: '0 6px 0 0',
    borderRadius: '16px !important',
    overflow: 'auto',
    backgroundColor: '#fff',
    '& .MuiAutocomplete-option': {
      fontSize: '1rem',
      padding: theme.spacing(2, 2.5),
      '&:hover': { backgroundColor: '#f0f0f0' },
      '&[aria-selected="true"]': { backgroundColor: '#e0e0e0' },
    },
  },
}));

interface ObjectAutocompleteProps {
  objects: IFilesApiObject[];
  selected: IFilesApiObject | null;
  inputValue: string;
  onChange: (value: IFilesApiObject | null) => void;
  onInputChange: (value: string) => void;
  placeholder?: string;
}

const ObjectAutocomplete: React.FC<ObjectAutocompleteProps> = ({
  objects,
  selected,
  inputValue,
  onChange,
  onInputChange,
  placeholder,
}) => {
  const classes = useStyles();
  const { formatMessage, locale } = useIntl();

  const getLocalizedName = useCallback((option: IFilesApiObject) => {
    const currentLocale = locale.split('-')[0];
    if (option.localization) {
      return option.localization[currentLocale] || option.localization.en || option.name;
    }
    return option.name;
  }, [locale]);
  const compareLocalizedLabels = useMemo(() => createLocalizedLabelComparator(locale), [locale]);
  const sortedObjects = useMemo(
    () =>
      [...objects].sort((objectA, objectB) => {
        const labelComparison = compareLocalizedLabels(getLocalizedName(objectA), getLocalizedName(objectB));

        return labelComparison || objectA.id - objectB.id;
      }),
    [objects, compareLocalizedLabels, getLocalizedName],
  );

  return (
    <Autocomplete
      options={sortedObjects}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      getOptionLabel={(o) => getLocalizedName(o)}
      
      fullWidth
      value={selected}
      inputValue={inputValue}
      // Explicitly pass values to ensure type safety
      onChange={(_, v) => onChange(v)}
      onInputChange={(_, val) => onInputChange(val)}
      noOptionsText=""
      renderInput={(params) => (
        <TextField
          {...params}
          label={placeholder || formatMessage({ id: 'ObjectAutocomplete.placeholder', defaultMessage: 'Objekt auswählen' })}
          variant="outlined"
          fullWidth
        />
      )}
      renderOption={(props, option) => {
        const name = getLocalizedName(option);
        
        const li = name.toLowerCase();
        const iv = inputValue.toLowerCase();
        const idx = li.indexOf(iv);
        
        if (idx < 0 || !inputValue) {
          return (
            <li {...props} key={option.id} style={{ color: '#464646' }}>
              {name}
            </li>
          );
        }
        
        const before = name.slice(0, idx);
        const match = name.slice(idx, idx + inputValue.length);
        const after = name.slice(idx + inputValue.length);
        
        return (
          <li {...props} key={option.id} style={{ color: '#464646' }}>
            {before}
            <span style={{ color: '#009fe3' }}>{match}</span>
            {after}
          </li>
        );
      }}
      PopperComponent={(props) => (
        <Popper
          {...props}
          className={classes.popper}
          style={{ width: '100%', ...props.style }}
          modifiers={[{ name: 'computeStyles', options: { adaptive: false } }]}
        />
      )}
      PaperComponent={(p) => <Paper {...p} className={classes.paper} />}
      ListboxProps={{ className: classes.listbox }}
      className={classes.autocomplete}
    />
  );
};

export default ObjectAutocomplete;
